import { Link } from 'react-router-dom';
import GlassButton from '@/components/GlassButton';
import GlassCard from '@/components/GlassCard';
import { motion } from 'framer-motion';

const ActivityIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const TargetIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const ShieldIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const ZapIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

export default function HomePage() {
  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Background Ambience */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-teal/10 via-bg-primary to-bg-primary" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-teal/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-plum/5 blur-[100px] rounded-full pointer-events-none" />
      </div>

      {/* Hero Section */}
      <div className="relative z-10 pt-32 pb-20 sm:pt-40 sm:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <h1 className="text-hero-title mb-6 text-text-primary">
                <span className="block text-text-secondary">Institutional Edge.</span>
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-brand-teal-light to-brand-teal">
                  Intelligent Execution.
                </span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-text-secondary max-w-2xl mx-auto font-light leading-relaxed">
                Experience the next generation of algorithmic wealth management. Deploy bespoke A2A strategies, capture real-time alpha, and optimize portfolios with deterministic precision.
              </p>
              
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
                <GlassButton asChild variant="primary" size="lg" glowEffect glowBlur="strong">
                  <Link to="/auth/register">Initialize Portfolio</Link>
                </GlassButton>
                <GlassButton asChild variant="ghost" size="lg">
                  <Link to="/glass-demo">Explore Architecture</Link>
                </GlassButton>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative z-10 py-24 border-t border-border-primary bg-bg-secondary/50 backdrop-blur-3xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-xs font-bold tracking-[0.2em] text-brand-teal uppercase mb-3">System Architecture</h2>
            <p className="text-3xl font-semibold tracking-tight text-text-primary">
              Engineered for absolute market supremacy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <GlassCard variant="elevated" className="p-8">
              <div className="h-12 w-12 rounded-full bg-brand-teal/10 flex items-center justify-center mb-6 border border-brand-teal/20">
                <ActivityIcon className="w-6 h-6 text-brand-teal" />
              </div>
              <h3 className="text-xl font-medium text-text-primary mb-3">Quantum-Speed Signals</h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                Proprietary deep-learning models sweep alternative data nodes milliseconds ahead of traditional retail terminals.
              </p>
            </GlassCard>

            <GlassCard variant="elevated" className="p-8">
              <div className="h-12 w-12 rounded-full bg-brand-plum/10 flex items-center justify-center mb-6 border border-brand-plum/20">
                <TargetIcon className="w-6 h-6 text-brand-plum-light" />
              </div>
              <h3 className="text-xl font-medium text-text-primary mb-3">A2A Execution</h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                Deploy autonomous trading agents that negotiate, hedge, and execute complex multi-asset spreads simultaneously.
              </p>
            </GlassCard>

            <GlassCard variant="elevated" className="p-8">
              <div className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/10">
                <ShieldIcon className="w-6 h-6 text-text-secondary" />
              </div>
              <h3 className="text-xl font-medium text-text-primary mb-3">Fort Knox Security</h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                Institutional-grade cold storage heuristics and predictive risk-management fail-safes protect capital in highly volatile environments.
              </p>
            </GlassCard>

            <GlassCard variant="elevated" className="p-8">
              <div className="h-12 w-12 rounded-full bg-brand-teal/10 flex items-center justify-center mb-6 border border-brand-teal/20">
                <ZapIcon className="w-6 h-6 text-brand-teal" />
              </div>
              <h3 className="text-xl font-medium text-text-primary mb-3">Synthetic Asset Routing</h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                Bridge traditional equities, crypto, and tokenized forex instantly through ultra-low latency synthetic liquidity pools.
              </p>
            </GlassCard>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 py-32 border-t border-border-primary">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-text-primary mb-6">
            Command Your Wealth.
          </h2>
          <p className="text-xl text-text-secondary mb-10 font-light">
            Step into the command center built for modern titans.
          </p>
          <GlassButton asChild variant="primary" size="xl" glowEffect glowBlur="stronger">
            <Link to="/auth/register">Request Platform Access</Link>
          </GlassButton>
        </div>
      </div>
    </div>
  );
}
